import React from 'react';

import { cn } from '../../lib/utils';

// Shared building blocks for the Partner Portal's dark premium theme — extends
// the referral brand (#38383B shell) with tile, badge, and skeleton recipes so
// every page reads identically.

export const TILE = 'rounded-xl bg-[#4A4A4E] shadow-md';
export const TILE_PAD = `${TILE} p-4 sm:p-5`;

export const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const money = (amount) => usd.format(Number(amount || 0));

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Reward-centric stages (server vocabulary) -> label + dark-theme badge tone.
const STAGES = {
  received: { label: 'Received', cls: 'bg-white/10 text-neutral-300' },
  in_progress: { label: 'In progress', cls: 'bg-blue-500/20 text-blue-300' },
  reward_earned: { label: 'Reward earned', cls: 'bg-amber-500/20 text-amber-300' },
  paid: { label: 'Paid', cls: 'bg-emerald-500/20 text-emerald-300' },
  closed: { label: 'Closed', cls: 'bg-white/10 text-neutral-300' },
  // Every claim for this referral was marked dead (Nic 2026-09-21): the one
  // red badge in the set, so it never reads as "still moving".
  dnf: { label: 'DNF', title: 'Did not finish', cls: 'bg-red-500/20 text-red-300' },
  // Payout statuses share the visual system.
  owed: { label: 'Owed', cls: 'bg-amber-500/20 text-amber-300' },
  void: { label: 'Void', cls: 'bg-white/10 text-neutral-300' },
};

export const StageBadge = ({ stage }) => {
  // Unknown server vocabulary falls back to the raw text, never a wrong label.
  const s = STAGES[stage] || { label: stage || '—', cls: 'bg-white/10 text-neutral-300' };
  return (
    <span
      // `title` is hover-only; the aria-label carries the expansion on touch
      // devices and to screen readers, where a bare "DNF" would be opaque.
      title={s.title}
      aria-label={s.title ? `${s.label}, ${s.title}` : undefined}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        s.cls,
      )}
    >
      {s.label}
    </span>
  );
};

export const StatTile = ({ icon: Icon, label, value, loading }) => (
  <div className={cn(TILE, 'p-4')}>
    {loading ? (
      <div className="space-y-2">
        <div className="h-7 w-16 animate-pulse rounded-md bg-white/10" />
        <div className="h-3.5 w-20 animate-pulse rounded-md bg-white/10" />
      </div>
    ) : (
      <>
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-white">{value}</span>
          {Icon ? <Icon className="h-5 w-5 text-neutral-400" /> : null}
        </div>
        <div className="mt-1 text-xs font-medium uppercase tracking-wide text-neutral-400">
          {label}
        </div>
      </>
    )}
  </div>
);

export const SkeletonRow = ({ className }) => (
  <div className={cn(TILE, 'p-4', className)}>
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <div className="h-4 w-36 animate-pulse rounded-md bg-white/10" />
        <div className="h-3 w-24 animate-pulse rounded-md bg-white/10" />
      </div>
      <div className="h-6 w-20 animate-pulse rounded-full bg-white/10" />
    </div>
  </div>
);

export const EmptyState = ({ children }) => (
  <div className={cn(TILE, 'px-6 py-10 text-center text-sm text-neutral-400')}>{children}</div>
);

// A failed query must NEVER render as an empty/zeroed state — a partner being
// told "$0 owed" because a request failed is a trust killer. Visible error +
// retry instead.
export const ErrorState = ({ onRetry }) => (
  <div className={cn(TILE, 'flex flex-col items-center gap-3 px-6 py-10 text-center')}>
    <p className="mb-0 text-sm text-neutral-300">
      We couldn&apos;t load this right now — please try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="min-h-[44px] rounded-md bg-white/10 px-6 text-sm font-medium text-white transition-colors hover:bg-white/15"
    >
      Retry
    </button>
  </div>
);

// Simple prev/next pager, thumb-sized targets. Disables while the next page is
// fetching (keepPreviousData shows old rows otherwise with zero feedback) and
// scrolls back to the top so the new page starts at row 1 on mobile.
export const Pager = ({ page, totalPages, onPage, isFetching = false }) => {
  if (totalPages <= 1) return null;
  const go = (p) => {
    onPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <div className="mt-4 flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={page <= 1 || isFetching}
        onClick={() => go(page - 1)}
        className="min-h-[44px] rounded-md bg-white/10 px-5 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:opacity-40"
      >
        Previous
      </button>
      <span className="text-xs text-neutral-400">
        {isFetching ? '…' : `${page} / ${totalPages}`}
      </span>
      <button
        type="button"
        disabled={page >= totalPages || isFetching}
        onClick={() => go(page + 1)}
        className="min-h-[44px] rounded-md bg-white/10 px-5 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
};
